import React, { useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaFilePdf, FaSpinner, FaTrash, FaEye, FaTimes, FaLink } from 'react-icons/fa'; 
import { API_URL, SITE_URL } from './config';
import { ROLES } from './permissions';

import MazeBoard from './MazeBoard';
import SudokuBoard from './SudokuBoard';
import WordSearchBoard from './WordSearchBoard';
import MathBoard from './MathBoard';
import WordScrambleBoard from './WordScrambleBoard';
import NumberPyramidBoard from './NumberPyramidBoard';
import BingoBoard from './BingoBoard';
import CrosswordBoard from './CrosswordBoard';
import SnakesBoard from './SnakesBoard';
import JigsawBoard from './JigsawBoard';

const BookPreview = ({ pages, removePage }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [bookId, setBookId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);
  const [trimSize, setTrimSize] = useState('pocket'); 

  const user = JSON.parse(localStorage.getItem('currentUser')) || { id: 'guest', role: ROLES.FREE };

  // ??????? ????? ?????? KDP ?? ????? ??????? ???? QR
  const sizeConfigs = {
    pocket: { width: 127, height: 203.2, label: '5" x 8"', contentWidth: 95, qrWidth: 85 },
    standard: { width: 152.4, height: 228.6, label: '6" x 9"', contentWidth: 115, qrWidth: 105 },
    large: { width: 215.9, height: 279.4, label: '8.5" x 11"', contentWidth: 165, qrWidth: 145 }
  };

  const handleClearAll = () => {
    if(window.confirm("Are you sure you want to delete ALL pages?")) {
      const userKey = `myBook_${user.id}`;
      localStorage.removeItem(userKey); 
      window.location.reload(); 
    }
  };

  const RenderGame = ({ page }) => {
    const props = { ...page.settings, seed: page.seed, interactive: false, showSolution: false };
    let GameComponent;
    switch (page.type) {
        case 'Maze': GameComponent = MazeBoard; break;
        case 'Sudoku': GameComponent = SudokuBoard; break;
        case 'WordSearch': GameComponent = WordSearchBoard; break;
        case 'Math': GameComponent = MathBoard; break;
        case 'WordScramble': GameComponent = WordScrambleBoard; break;
        case 'NumberPyramid': GameComponent = NumberPyramidBoard; break;
        case 'Bingo': GameComponent = BingoBoard; break;
        case 'Crossword': GameComponent = CrosswordBoard; break;
        case 'Snakes': GameComponent = SnakesBoard; break;
        case 'Jigsaw': GameComponent = JigsawBoard; break;
        default: return <div style={{color:'red'}}>Unknown</div>;
    }
    return <div className="game-capture-zone"><GameComponent {...props} /></div>;
  };

  const handleSaveAndDownload = async () => {
    if (pages.length === 0) return alert("Book is empty!");
    setIsSaving(true);
    try {
      const bookData = {
          userId: user.id,
          puzzles: pages.map(p => ({ id: p.id, type: p.type, seed: p.seed, settings: p.settings }))
      };
      const res = await axios.post(`${API_URL}/books`, bookData);
      setBookId(res.data.bookId); 
      const qrUrl = await QRCode.toDataURL(`${SITE_URL}/solutions?bookId=${res.data.bookId}`);
      setQrCode(qrUrl);
      await new Promise(r => setTimeout(r, 2000)); 
      await generatePDF(res.data.bookId);
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
    }
  };

  const generatePDF = async (id) => {
    const { width: pWidth, height: pHeight, contentWidth, qrWidth } = sizeConfigs[trimSize];
    const doc = new jsPDF('p', 'mm', [pWidth, pHeight]);
    const currentYear = new Date().getFullYear();

    // 1. PAGE 1 - TITLE PAGE
    doc.setFont("helvetica", "bold");
    doc.setFontSize(trimSize === 'large' ? 36 : 28);
    doc.text("PUZZLIFY BOOK", pWidth / 2, pHeight / 2 - 20, { align: 'center' });
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    // ? ?? ????? ???? ??? ????? ??? ??? ???????
    doc.text(`${pages.length} Puzzles`, pWidth / 2, pHeight / 2, { align: 'center' });
    doc.setFontSize(10);
    doc.text("Created by Puzzlify.net", pWidth / 2, pHeight - 20, { align: 'center' });

    // 2. PAGE 2 - COPYRIGHT
    doc.addPage();
    doc.setFontSize(10);
    doc.text(`Copyright \u00A9 ${currentYear} Puzzlify.net`, pWidth / 2, pHeight / 2 - 10, { align: 'center' });

    // 3. PAGE 3 - QR CODE (Dynamic Scaling ?)
    doc.addPage();
    const cover = document.getElementById('hidden-cover');
    cover.style.width = `${pWidth}mm`;
    cover.style.height = `${pHeight}mm`;
    const c = await html2canvas(cover, { scale: 2, useCORS: true }); 
    const qrH = (c.height * qrWidth) / c.width;
    doc.addImage(c.toDataURL('image/jpeg', 1.0), 'JPEG', (pWidth - qrWidth) / 2, (pHeight - qrH) / 2 - 10, qrWidth, qrH);
    doc.setFontSize(10);
    doc.text(`Scan for Solutions - ID: ${id}`, pWidth / 2, pHeight - 25, { align: 'center' });

    // 4. PUZZLES PAGES
    const elements = document.getElementsByClassName('hidden-page');
    for (let i = 0; i < elements.length; i++) {
        doc.addPage();
        elements[i].style.width = `${pWidth}mm`;
        elements[i].style.height = `${pHeight}mm`;
        const target = elements[i].querySelector('.game-capture-zone') || elements[i];
        const el = await html2canvas(target, { scale: 3, backgroundColor: '#ffffff', useCORS: true });
        const imgProps = doc.getImageProperties(el.toDataURL('image/jpeg', 1.0));
        const cH = (imgProps.height * contentWidth) / imgProps.width;
        doc.addImage(el.toDataURL('image/jpeg', 1.0), 'JPEG', (pWidth - contentWidth) / 2, 35, contentWidth, cH);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text(`${elements[i].getAttribute('data-title')} #${i + 1}`, pWidth / 2, 20, { align: 'center' });
        doc.setFontSize(9);
        doc.text(`Page ${i + 1}`, pWidth / 2, pHeight - 10, { align: 'center' });
    }
    doc.save(`Puzzlify_${trimSize}_${id}.pdf`);
  };

  const openSolutions = () => { if (bookId) window.open(`${SITE_URL}/solutions?bookId=${bookId}`, '_blank'); };

  return (
    <div style={{ padding: 20 }}>
      <style>{`
        .preview-card button:not(.action-btn), .preview-card select, .preview-card input:not([type="text"]),
        .preview-card .controls, .preview-card .solution-badge,
        .hidden-page button, .hidden-page select, .hidden-page .controls { display: none !important; }
        .game-capture-zone h1, .game-capture-zone h2, .game-capture-zone h3, .game-capture-zone table, .game-capture-zone .sr-only {
             display: none !important; opacity: 0 !important; visibility: hidden !important;
             position: absolute !important; height: 0 !important; width: 0 !important;
        }
        .game-capture-zone { display: inline-block; padding: 20px; background: white; border-radius: 4px; font-size: 1.2em; }
        .game-capture-zone canvas, .game-capture-zone svg { display: block; border: 3px solid #000; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.8); display: flex; justify-content: center; align-items: center; z-index: 1000; }
        .modal-content { background: white; padding: 20px; border-radius: 8px; max-width: 90%; max-height: 90%; overflow: auto; position: relative; }
        .close-modal { position: absolute; top: 10px; right: 10px; background: none; border: none; cursor: pointer; font-size: 20px; }
      `}</style>

      <div style={{ background: 'white', padding: 15, borderRadius: 8, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <h2>Book Preview ({pages.length} Puzzles)</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f1f2f6', padding: '5px 10px', borderRadius: '5px' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Size:</span>
                <select value={trimSize} onChange={(e) => setTrimSize(e.target.value)} style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}>
                    <option value="pocket">5" x 8" (Pocket)</option>
                    <option value="standard">6" x 9" (Standard)</option>
                    <option value="large">8.5" x 11" (Large Print)</option>
                </select>
            </div>
            {pages.length > 0 && (
                <button onClick={handleClearAll} style={{ background: '#d63031', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold' }}><FaTrash /> Clear</button>
            )}
            {bookId && (
                <button onClick={openSolutions} style={{ background: '#2ecc71', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 5, cursor: 'pointer' }}><FaLink /> Solutions</button>
            )}
            <button onClick={handleSaveAndDownload} disabled={isSaving || pages.length === 0} style={{ background: '#0984e3', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 5, cursor: 'pointer', fontWeight: 'bold' }}>
                {isSaving ? <><FaSpinner className="icon-spin"/> Building...</> : <><FaFilePdf /> Export PDF</>}
            </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15 }}>
        {pages.map((page, index) => (
          <div key={page.id} className="preview-card" style={{ border: '1px solid #ddd', background: 'white', padding: 10, borderRadius: 8, width: 220, position: 'relative' }}>
            <strong style={{display:'block', marginBottom:5}}>#{index + 1} - {page.type}</strong>
            <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', height: 200, width: 400, overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <RenderGame page={page} />
            </div>
            <div style={{ position: 'absolute', top: 5, right: 5, display: 'flex', gap: '5px' }}>
                <button className="action-btn" onClick={() => setPreviewPage(page)} style={{ background: '#0984e3', color: 'white', border: 'none', borderRadius: '50%', width: 25, height: 25 }}><FaEye size={12}/></button>
                <button className="action-btn" onClick={() => removePage(page.id)} style={{ background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: 25, height: 25 }}><FaTrash size={12}/></button>
            </div>
          </div>
        ))}
      </div>

      {previewPage && (
        <div className="modal-overlay" onClick={() => setPreviewPage(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="close-modal action-btn" onClick={() => setPreviewPage(null)}><FaTimes /></button>
                <h3 style={{ marginBottom: '15px', textAlign:'center' }}>{previewPage.type}</h3>
                <RenderGame page={previewPage} />
            </div>
        </div>
      )}

      {/* Hidden container for PDF capture */}
      <div style={{ position: 'fixed', left: '-10000px', top: 0 }}>
        <div id="hidden-cover" style={{ background: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <h1 style={{ fontSize: 40, fontWeight: '900' }}>PUZZLIFY</h1>
            <h2 style={{ fontSize: 20 }}>Scan Solutions</h2>
            {qrCode && <img src={qrCode} width={250} alt="QR" />}
        </div>
        {pages.map((p, i) => (
            <div key={p.id} className="hidden-page" data-title={`${p.type}`} style={{ background: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent:'center' }}>
                <RenderGame page={p} />
            </div>
        ))}
      </div>
    </div>
  );
};

export default BookPreview;